import React, { useEffect, useMemo, useState } from "react";
import {
  addDays,
  compareAsc,
  format,
  isAfter,
  isSameWeek,
  parseISO,
  subDays,
} from "date-fns";
import { Loader2, Calendar, CheckCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  fetchAppointments,
  fetchSoapNotes,
} from "../../lib/therapistApi";

function useTherapistId() {
  const { therapistId } = useAuth();
  return therapistId;
}

const toDate = (value) =>
  typeof value === "string" ? parseISO(value) : new Date(value);

const Reports = () => {
  const therapistId = useTherapistId();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!therapistId) {
      console.warn("No therapist ID available, cannot load analytics");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const start = format(subDays(new Date(), 14), "yyyy-MM-dd");
        const end = format(addDays(new Date(), 21), "yyyy-MM-dd");

        const [apptsRes, notesRes] = await Promise.all([
          fetchAppointments(therapistId, { from: start, to: end }),
          fetchSoapNotes(therapistId).catch(() => ({ notes: [] })),
        ]);

        setAppointments(apptsRes.appointments || []);
        setNotes(notesRes.notes || []);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, [therapistId]);

  const metrics = useMemo(() => {
    const now = new Date();
    const upcoming = appointments.filter(
      (a) =>
        isAfter(toDate(a.appointmentDate), now) && a.status === "confirmed",
    );
    const pending = appointments.filter((a) => a.status === "pending");
    const completedThisWeek = appointments.filter((a) => {
      const date = toDate(a.appointmentDate);
      return isSameWeek(date, now) && a.status === "completed";
    });

    const notesDraft = notes.filter((n) => n.isDraft || !n.signedAt);
    const notesSigned = notes.filter((n) => n.signedAt);

    return {
      upcoming: upcoming.length,
      pending: pending.length,
      completedThisWeek: completedThisWeek.length,
      drafts: notesDraft.length,
      signed: notesSigned.length,
    };
  }, [appointments, notes]);

  const nextAppointments = useMemo(() => {
    const now = new Date();
    return appointments
      .filter((a) => isAfter(toDate(a.appointmentDate), now))
      .sort((a, b) =>
        compareAsc(toDate(a.appointmentDate), toDate(b.appointmentDate)),
      )
      .slice(0, 5);
  }, [appointments]);

  const outstandingNotes = useMemo(
    () => notes.filter((n) => !n.signedAt).slice(0, 5),
    [notes],
  );

  if (!therapistId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-gray-600">
        Therapist ID missing. Log in again to continue.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Practice Insights</h1>
        <p className="text-slate-600 mt-2">
          Snapshot of booking activity, open tasks, and setting coverage across
          the next few weeks.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium text-slate-600">
                  Upcoming confirmed
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {metrics.upcoming}
            </p>
            <p className="text-sm text-slate-500 mt-1">Next 21 days</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium text-slate-600">
                  Pending approvals
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {metrics.pending}
            </p>
            <p className="text-sm text-slate-500 mt-1">Awaiting confirmation</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium text-slate-600">
                  Completed this week
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {metrics.completedThisWeek}
            </p>
            <p className="text-sm text-slate-500 mt-1">Mon–Sun</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium text-slate-600">
                  SOAP drafts
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {metrics.drafts}
            </p>
            <p className="text-sm text-slate-500 mt-1">Pending signature</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            Outstanding SOAP notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {outstandingNotes.length ? (
            outstandingNotes.map((note) => (
              <div
                key={note._id}
                className="rounded-md border p-3 text-xs text-gray-600">
                <div className="flex items-center justify-between">
                  <span>Draft #{note.version}</span>
                  <span>
                    {format(parseISO(note.updatedAt), "MMM d, h:mm a")}
                  </span>
                </div>
                {note.clientEmail && (
                  <div className="mt-1">Client: {note.clientEmail}</div>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">All notes are signed ✨</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            Next confirmed sessions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {nextAppointments.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nextAppointments.map((appt) => (
                  <TableRow key={String(appt.id)}>
                    <TableCell>
                      {format(toDate(appt.appointmentDate), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      {format(toDate(appt.appointmentDate), "h:mm a")}
                    </TableCell>
                    <TableCell className="capitalize">{appt.status}</TableCell>
                    <TableCell>{appt.clientEmail}</TableCell>
                    <TableCell>{appt.duration} min</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-6 text-center text-sm text-gray-500">
              No upcoming appointments.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
